import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import {
  getCurrentUser,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import { Loader, View } from "@aws-amplify/ui-react";
import type { Schema } from "@backend-schema";
import { getCachedAuthSession, getCachedUserAttributes } from "../utils/userAttributesCache";

interface CompanyAdminRouteProps {
  children: ReactNode;
}

/**
 * Route wrapper that allows access to:
 * 1. SkillBubble admins (Cognito 'admin' group)
 * 2. Company admins (verified company email + active company subscription)
 */
function CompanyAdminRoute({ children }: CompanyAdminRouteProps) {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      await getCurrentUser();
      const session = await getCachedAuthSession();

      // Check if SkillBubble admin
      const idToken = session.tokens?.idToken;
      const groups = idToken?.payload["cognito:groups"] as string[] | undefined;
      const isSkillBubbleAdmin = groups?.includes("admin") || false;

      if (isSkillBubbleAdmin) {
        setHasAccess(true);
        return;
      }

      // Check if company admin with active subscription
      const attributes = await getCachedUserAttributes();
      const companyEmailVerified =
        attributes["custom:company_verified"] === "true";
      const companyDomain = attributes["custom:company_domain"];

      if (companyEmailVerified && companyDomain) {
        const client = generateClient<Schema>();
        const subscriptionResult = await client.models.CompanySubscription.list(
          {
            filter: {
              companyDomain: { eq: companyDomain },
              subscriptionStatus: { eq: "active" },
            },
          },
        );

        setHasAccess(
          subscriptionResult.data && subscriptionResult.data.length > 0,
        );
      } else {
        setHasAccess(false);
      }
    } catch {
      setHasAccess(false);
    }
  };

  if (hasAccess === null) {
    return (
      <View padding="2rem" textAlign="center">
        <Loader size="large" />
      </View>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}

export default CompanyAdminRoute;
